/**
 * Where the person is, and what they have selected.
 *
 * WHY THIS IS SEPARATE FROM THE REPLICA
 *   The replica (workspace.ts) is shared truth that arrives from the server.
 *   This is private, per-window, throwaway state. Keeping them apart means a
 *   sync delta never re-renders a scroll position, and changing a selection
 *   never risks touching data.
 *
 * DATA DEFINITIONS
 *
 *   A View is "files" | "activity" | "comments" | "locks" | "sync".
 *   Interpretation: the five ways to look at a project — what exists now,
 *   what has happened, what people are saying about it, what is claimed, and
 *   how it differs from a folder on this machine.
 *
 *   A Selection is a Set of path strings within ONE project.
 *   Interpretation: what a bulk action (download, delete) would apply to.
 *   Cleared whenever the project changes, because a path means nothing in a
 *   project it does not belong to.
 *
 *   The LOCATION is (projectId, view, path?) and is mirrored into the URL
 *   hash, so a link to a drawing can be pasted into a comment and reloading
 *   returns you to exactly where you were.
 *
 *   A SortBook is a Map from project id to SortOrder.
 *   Interpretation: how each project's tree is listed. Per project because
 *   the choice belongs to the material: a drawings project is read by name,
 *   an active build is read by date modified. It is the one piece of this
 *   store that OUTLIVES the window — it is written to localStorage — because
 *   re-choosing it after every reload is the sort of small tax that makes a
 *   tool feel disposable.
 */
import { create } from 'zustand'
import {
  type ProjectId, type SortOrder,
  DEFAULT_SORT, formatSortOrder, parseSortOrder,
} from '@lattice/core'

export type View = 'files' | 'activity' | 'comments' | 'locks' | 'sync'

export interface UiState {
  projectId?: ProjectId
  view: View
  /** The file whose detail panel and preview are showing. */
  openPath?: string
  /** Paths ticked for a bulk action. */
  selection: Set<string>
  /** The path last clicked, so shift-click can extend from it. */
  anchor?: string
  expanded: Set<string>
  detailOpen: boolean
  paletteOpen: boolean
  /** A filter applied to the tree, distinct from the command palette. */
  filter: string
  /** How each project's tree is listed; absent means DEFAULT_SORT. */
  sort: Record<string, SortOrder>

  go: (to: { projectId?: ProjectId; view?: View; openPath?: string | undefined }) => void
  setView: (view: View) => void
  open: (path: string | undefined) => void
  toggleExpanded: (path: string) => void
  setExpanded: (paths: Iterable<string>) => void
  expandTo: (path: string) => void
  select: (paths: Iterable<string>, mode?: 'replace' | 'add' | 'toggle') => void
  clearSelection: () => void
  setAnchor: (path: string | undefined) => void
  setDetailOpen: (open: boolean) => void
  setPaletteOpen: (open: boolean) => void
  setFilter: (filter: string) => void
  setSort: (projectId: string, order: SortOrder) => void
}

/* ------------------------------------------------------------------ */
/* Persisting the sort choice                                          */
/* ------------------------------------------------------------------ */

const SORT_KEY_PREFIX = 'lattice.sort.'

/**
 * Every sort choice this browser has saved, read once at start-up.
 * Reading all of them up front (rather than lazily per project) keeps the
 * store a plain synchronous value: a few keys in localStorage cost
 * microseconds, and it means switching projects never has to hydrate.
 * readSavedSorts : -> {projectId: SortOrder}
 */
function readSavedSorts(): Record<string, SortOrder> {
  const out: Record<string, SortOrder> = {}
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key?.startsWith(SORT_KEY_PREFIX)) continue
      out[key.slice(SORT_KEY_PREFIX.length)] = parseSortOrder(localStorage.getItem(key))
    }
  } catch {
    // Private browsing, or storage disabled. Every project then opens in the
    // default order, which is the order it opened in before this feature.
  }
  return out
}

/** Remember a choice for next time. saveSort : String SortOrder -> void */
function saveSort(projectId: string, order: SortOrder): void {
  try {
    localStorage.setItem(`${SORT_KEY_PREFIX}${projectId}`, formatSortOrder(order))
  } catch { /* the choice simply will not survive a reload */ }
}

/** How a project is listed right now. sortFor : UiState ProjectId -> SortOrder */
export const sortFor = (state: UiState, projectId: string | undefined): SortOrder =>
  (projectId ? state.sort[projectId] : undefined) ?? DEFAULT_SORT

/** Ancestors of a path, so opening a deep file reveals it in the tree. */
function ancestors(path: string): string[] {
  const parts = path.split('/')
  return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'))
}

export const useUi = create<UiState>()((set) => ({
  view: 'files',
  selection: new Set<string>(),
  expanded: new Set<string>(),
  detailOpen: true,
  paletteOpen: false,
  filter: '',
  sort: readSavedSorts(),

  go: to => set(state => {
    const changingProject = to.projectId !== undefined && to.projectId !== state.projectId
    return {
      projectId: to.projectId ?? state.projectId,
      view: to.view ?? state.view,
      openPath: 'openPath' in to ? to.openPath : state.openPath,
      // A selection is meaningless in another project.
      selection: changingProject ? new Set<string>() : state.selection,
      expanded: changingProject ? new Set<string>() : state.expanded,
      filter: changingProject ? '' : state.filter,
    }
  }),

  setView: view => set({ view }),

  open: path => set(state => ({
    openPath: path,
    expanded: path
      ? new Set([...state.expanded, ...ancestors(path)])
      : state.expanded,
  })),

  toggleExpanded: path => set(state => {
    const next = new Set(state.expanded)
    if (next.has(path)) next.delete(path)
    else next.add(path)
    return { expanded: next }
  }),

  setExpanded: paths => set({ expanded: new Set(paths) }),

  expandTo: path => set(state => ({ expanded: new Set([...state.expanded, ...ancestors(path)]) })),

  select: (paths, mode = 'replace') => set(state => {
    if (mode === 'replace') return { selection: new Set(paths) }
    const next = new Set(state.selection)
    for (const p of paths) {
      if (mode === 'toggle' && next.has(p)) next.delete(p)
      else next.add(p)
    }
    return { selection: next }
  }),

  clearSelection: () => set({ selection: new Set<string>() }),
  setAnchor: anchor => set({ anchor }),
  setDetailOpen: detailOpen => set({ detailOpen }),
  setPaletteOpen: paletteOpen => set({ paletteOpen }),
  setFilter: filter => set({ filter }),

  setSort: (projectId, order) => {
    // Persist OUTSIDE the updater. A zustand updater may be invoked more than
    // once for a single logical update, so a side effect inside it is a side
    // effect that can happen twice.
    saveSort(projectId, order)
    set(state => ({ sort: { ...state.sort, [projectId]: order } }))
  },
}))

/* ------------------------------------------------------------------ */
/* URL synchronisation                                                 */
/* ------------------------------------------------------------------ */

/**
 * Encode a location into a hash.
 * Interpretation: "#/LCSEM2/files/Cathode_Cup_Assembly/Roof.PDF". The path is
 * encoded per segment so spaces and hashes in real CAD filenames survive.
 * toHash : {projectId, view, openPath} -> String
 */
export function toHash(loc: { projectId?: string; view: View; openPath?: string }): string {
  if (!loc.projectId) return '#/'
  const parts = ['#', loc.projectId, loc.view]
  if (loc.openPath) parts.push(...loc.openPath.split('/').map(encodeURIComponent))
  return parts.join('/')
}

/** Decode a hash back into a location. fromHash : String -> Location|undefined */
export function fromHash(hash: string): { projectId: ProjectId; view: View; openPath?: string } | undefined {
  const parts = hash.replace(/^#\/?/, '').split('/').filter(Boolean)
  if (parts.length === 0) return undefined
  const [projectId, view, ...rest] = parts
  const known: View[] = ['files', 'activity', 'comments', 'locks', 'sync']
  return {
    projectId: projectId as ProjectId,
    view: known.includes(view as View) ? (view as View) : 'files',
    openPath: rest.length ? rest.map(decodeURIComponent).join('/') : undefined,
  }
}
